import React, { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useOfferForm } from '../utils/useOfferForm';
import { CalendarDays, LockKeyhole, Mail, MapPin, Phone, Smartphone, UserRound, Utensils } from 'lucide-react';
import './BigBreakfastPage.css';
import './RestaurantOfferPage.css';

const GoodTimesBadge: React.FC = () => <div className="csb-badge"><Utensils aria-hidden="true" /><span>Great food<br />Cold drinks<br />Good times</span><span aria-hidden="true">★</span></div>;

const RestaurantOfferPage: React.FC<{ thankYou?: boolean }> = ({ thankYou = false }) => {
  const location = useLocation();
  const { register, handleSubmit, onSubmit, isPending, submitError, formState: { errors } } = useOfferForm('CSB10');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = thankYou ? 'Thank You | The Creamery Restaurant Offer' : '10% Off Your Next Meal | The Creamery';
    return () => { document.title = previousTitle; };
  }, [thankYou]);

  if (thankYou && location.state?.offerQueued !== 'CSB10') return <Navigate to="/CSB10" replace />;

  return <main className={`bb-page csb-page${thankYou ? ' csb-thanks' : ''}`}>
    <div className="bb-poster csb-poster">
      <header className="csb-hero">
        {!thankYou && <img className="csb-meal" src="/images/food/steak-pie1.webp" alt="The Creamery steak pie with golden pastry, chips, peas and carrots" loading="eager" />}
        <Link to="/" className="csb-brand" aria-label="The Creamery home"><img src="/logo.webp" alt="The Creamery — Sports Bar, Restaurant, Snooker, Pool" /></Link>
        {!thankYou && <>
          <div className="csb-headline">
            <h1><span className="csb-claim">★ Claim ★</span><strong>10% off</strong><span className="csb-brush">Your next meal</span></h1>
            <p className="csb-at">at</p><p className="csb-venue">The Creamery Sports Bar</p>
            <p className="csb-script">Falkirk’s Best Kept Secret</p>
          </div>
          <GoodTimesBadge />
        </>}
      </header>

      {thankYou ? <section className="csb-confirmation" aria-labelledby="csb-thanks-title">
        <h1 id="csb-thanks-title">★ Thank you! ★</h1>
        <p className="csb-brush csb-all-set">Request received</p>
        <p className="bb-intro">Your offer request is queued for processing. Email and SMS delivery will follow once it has been processed.</p>
        <h2>You will receive an email with your <strong>10% off voucher</strong></h2>
        <p className="csb-voucher-intro">Your email will include a downloadable voucher<br className="bb-desktop-break" /> with a unique code that entitles you to <strong>10% off your next meal</strong></p>
        <div className="csb-divider" aria-hidden="true">✦</div>
        <div className="bb-delivery csb-delivery">
          <article><Mail aria-hidden="true" /><h3>Check your email</h3><p>Look out for an email from The Creamery Sports Bar with your 10% OFF voucher. Don’t forget to check your junk folder!</p></article>
          <article><Smartphone aria-hidden="true" /><h3>Text message</h3><p>We’ll also send you a text message with your unique discount code so you can use it with ease.</p></article>
        </div>
        <div className="csb-welcome"><GoodTimesBadge /><div><h2>Falkirk’s best kept secret</h2><p className="csb-script">Great Food, Cold Drinks, Good Times!</p><p>We can’t wait to welcome you to The Creamery.</p></div></div>
        <Link className="bb-back" to="/CSB10">Back to the offer</Link>
      </section> : <section className="csb-capture" aria-labelledby="csb-form-title">
        <h2 id="csb-form-title">Fill in your details below to claim <strong>10% off</strong> your next meal.</h2>
        <div className="csb-divider" aria-hidden="true">✦</div>
        <form onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Restaurant meal discount" aria-busy={isPending}>
          {([
            { name: 'firstName', label: 'First name', type: 'text', autoComplete: 'given-name', Icon: UserRound },
            { name: 'lastName', label: 'Surname', type: 'text', autoComplete: 'family-name', Icon: UserRound },
            { name: 'email', label: 'Email address', type: 'email', autoComplete: 'email', Icon: Mail },
            { name: 'mobile', label: 'Mobile number', type: 'tel', autoComplete: 'tel', Icon: Smartphone },
          ] as const).map(({ name, label, type, autoComplete, Icon }) => <div className="csb-field" key={name}>
            <label htmlFor={`csb-${name}`}><Icon aria-hidden="true" />{label}</label>
            <div className="bb-input-wrap"><input {...register(name)} id={`csb-${name}`} type={type} autoComplete={autoComplete} placeholder={`Enter your ${label.toLowerCase()}`} required aria-invalid={!!errors[name]} aria-describedby={errors[name] ? `csb-${name}-error` : undefined} />
              {errors[name] && <p className="bb-error" id={`csb-${name}-error`} role="alert">{errors[name]?.message}</p>}
            </div>
          </div>)}
          <div className="csb-actions">
            <p role="status" className="bb-form-note">{isPending ? 'Submitting your offer request…' : ''}</p>
            {submitError && <p className="bb-error" role="alert">{submitError}</p>}
            <button className="bb-submit" type="submit" disabled={isPending}>{isPending ? 'Submitting…' : 'Claim my 10% off'}</button>
            <p className="bb-security"><LockKeyhole aria-hidden="true" />We respect your privacy. Your details will never be shared.</p>
          </div>
        </form>
      </section>}

      <footer className="csb-footer">
        <div className="csb-info">
          <div><CalendarDays aria-hidden="true" /><p>Valid for a limited time<em>Not to be used in conjunction with any other offers</em></p></div>
          <div><MapPin aria-hidden="true" /><p>Main Street,<br />Bainsford,<br />Falkirk FK2 7NU</p></div>
          <a href="tel:01324636330"><Phone aria-hidden="true" /><span>Tel:<br />01324 636330</span></a>
        </div>
        <div className="csb-opening"><h2>★ Restaurant open ★</h2><p><span>Sunday – Friday <b>12pm – 7pm</b></span><span>Saturday <b>12pm – 7.30pm</b></span></p></div>
        {thankYou && <img className="csb-footer-meal" src="/images/food/steak-pie1.webp" alt="Steak pie served with chips and vegetables" loading="lazy" />}
        <div className="bb-legal"><Link to="/">The Creamery</Link><Link to="/privacy">Privacy Policy</Link><Link to="/terms">Terms of Service</Link></div>
      </footer>
    </div>
  </main>;
};

export default RestaurantOfferPage;
